import { AppData as LightningAppData, Settings } from '@lightningjs/sdk';
import { TizenDeviceIntegration } from 'config/platforms/tizen/TizenDeviceIntegration';
import { UnknownDeviceIntegration } from 'config/platforms/unknown/UnknownDeviceIntegration';

import { constants } from 'aliases';

/*
This is set by the rollup-virtual plugin during the "lng build" command
implementation of this can be found in: https://github.com/rdkcentral/Lightning-CLI/blob/master/src/configs/rollup.es6.config.js
related discussion: https://forum.lightningjs.io/t/accessing-environment-variables
*/
// @ts-ignore
import processEnv from 'processEnv';
import { Platform } from 'models/platforms/platform';
import PrivacyService from 'services/PrivacyService';
import RecentSearchService from 'services/RecentSearchService';
import StorageService from 'services/StorageService';
import { VizioDeviceIntegration } from 'config/platforms/vizio/VizioDeviceIntegration';
import { LGDeviceIntegration } from 'config/platforms/lg/LGDeviceIntegration';
import MigrationService from 'services/MigrationService';
import ParentalPinService from 'services/ParentalPinService';
import ModalService from 'services/ModalService';
import { loadBrightcoveSdkScript } from 'libraries/brightcoveSdk';
import { ComcastDeviceIntegration } from './platforms/comcast/ComcastDeviceIntegration';
import LoginRadiusService from 'services/LoginRadiusService';
import PersonaService from 'services/personaService';
import OfflineErrorService from 'services/OfflineErrorService';
import QrLoginService from 'services/QrLoginService';
import SmartLoginService from 'services/SmartLoginService';
import TizenLocalHistoryService from 'services/history/local/TizenLocalHistoryService';
import DefaultLocalHistoryService from 'services/history/local/DefaultLocalHistoryService';
import HistoryService from 'services/history/HistoryService';
import ListItemPromiseService from 'services/ListItemPromiseService';
import BrazeService from 'services/BrazeService';
import NotificationService from 'services/NotificationService';
import BlueconicService from 'services/BlueconicService';
import AdobePassService from 'services/AdobePassService';
import OneTrustService from 'services/OneTrustService';

export class AppData implements LightningAppData {
  readonly isProduction: LightningAppData['isProduction'];
  readonly debug: LightningAppData['debug'];
  readonly useCacheBackup: LightningAppData['useCacheBackup'];
  readonly privacyModifiedDate: LightningAppData['privacyModifiedDate'];
  readonly adSkipEnabled: LightningAppData['adSkipEnabled'];
  readonly hasService: LightningAppData['hasService'];
  readonly log: LightningAppData['log'];
  readonly device: LightningAppData['device'];
  readonly api: LightningAppData['api'];
  readonly devAccessHeader: LightningAppData['devAccessHeader'];
  readonly images: LightningAppData['images'];
  readonly google: LightningAppData['google'];
  readonly widevine: LightningAppData['widevine'];
  readonly login: LightningAppData['login'];

  readonly timers: LightningAppData['timers'];

  tivoEndpoints: LightningAppData['tivoEndpoints'];
  ayswIdleTimes: LightningAppData['ayswIdleTimes'];
  bookmarkHeartbeatIntervalSeconds: LightningAppData['bookmarkHeartbeatIntervalSeconds'];
  isDeepLinkEntry: LightningAppData['isDeepLinkEntry'];
  drmService: LightningAppData['drmService'];
  topNavCameFromNavBar: boolean;

  // Analytics
  brightcove!: LightningAppData['brightcove'];
  conviva!: LightningAppData['conviva'];
  comscore!: LightningAppData['comscore'];
  nielsen!: LightningAppData['nielsen'];
  oneTrust!: LightningAppData['oneTrust'];
  mParticle!: LightningAppData['mParticle'];

  // External Services
  adobePassService!: LightningAppData['adobePassService'];
  blueconicService!: LightningAppData['blueconicService'];
  personaService!: LightningAppData['personaService'];
  loginRadiusService!: LightningAppData['loginRadiusService'];
  brazeService!: LightningAppData['brazeService'];

  // Local Services
  announcerService: LightningAppData['announcerService'];
  historyService!: LightningAppData['historyService'];
  listItemPromiseService!: LightningAppData['listItemPromiseService'];
  localHistoryService: LightningAppData['localHistoryService'];
  migrationService!: LightningAppData['migrationService'];
  modalService!: LightningAppData['modalService'];
  notificationService!: LightningAppData['notificationService'];
  offlineErrorService!: LightningAppData['offlineErrorService'];
  oneTrustService!: LightningAppData['oneTrustService'];
  parentalPinService!: LightningAppData['parentalPinService'];
  privacyService!: LightningAppData['privacyService'];
  qrLoginService!: LightningAppData['qrLoginService'];
  recentSearchesService!: LightningAppData['recentSearchesService'];
  smartLoginService!: LightningAppData['smartLoginService'];
  storageService: LightningAppData['storageService'];

  constructor() {
    this.isProduction = processEnv.NODE_ENV === 'prod';
    this.isDeepLinkEntry = false;
    this.topNavCameFromNavBar = false;
    this.debug = processEnv.APP_DEBUG === 'true';
    this.useCacheBackup =
      !this.isProduction && processEnv.APP_USE_CACHE_BACKUP === 'true';
    this.privacyModifiedDate = '';
    this.adSkipEnabled = () => {
      return (
        processEnv.APP_USE_AD_SKIP === 'true' &&
        Settings.get('app', 'enableAdSkip', false)
      );
    };
    this.hasService = id => constants.services.includes(id);

    this.log = {
      debug: this.debug
        ? console.log
        : () => {
            /* no-op */
          },
    };

    const platform: Platform = processEnv.APP_PLATFORM;
    switch (platform) {
      case Platform.TIZEN:
        this.device = new TizenDeviceIntegration();
        break;
      case Platform.VIZIO:
        this.device = new VizioDeviceIntegration();
        break;
      case Platform.LG:
        this.device = new LGDeviceIntegration();
        break;
      case Platform.COMCAST:
        this.device = new ComcastDeviceIntegration();
        break;
      case Platform.UNKNOWN:
      default:
        this.device = new UnknownDeviceIntegration();
    }

    this.storageService = new StorageService(this.device);

    switch (platform) {
      case Platform.TIZEN:
        this.localHistoryService = new TizenLocalHistoryService({
          deviceIntegration: this.device as TizenDeviceIntegration,
          storageService: this.storageService,
        });
        break;
      case Platform.VIZIO:
      case Platform.LG:
      case Platform.UNKNOWN:
      default:
        this.localHistoryService = new DefaultLocalHistoryService({
          storageService: this.storageService,
        });
    }

    // These values will be set by the Announcer class
    this.announcerService = {
      getIsSpeaking: null,
      setOnSpeakEvent: null,
      removeOnSpeakEvent: null,
    } as any;

    this.api = {
      baseUrl: processEnv.APP_API_BASE_URL,
      staticCmsUrl: processEnv.APP_API_STATIC_CMS_URL,
      device: processEnv.APP_API_DEVICE_PARAMETER,
      username: processEnv.APP_API_USER,
      password: processEnv.APP_API_PASS,
    };

    this.devAccessHeader = {
      header: processEnv.APP_DEV_ACCESS_HEADER,
      key: processEnv.APP_DEV_ACCESS_KEY,
    };

    this.images = {
      dynamicBaseUrl: processEnv.APP_IMAGES_DYNAMIC_BASE_URL,
    };

    this.google = {
      apiKey: processEnv.APP_IMA_API_KEY,
      idtype: processEnv.APP_GOOGLE_ID_TYPE,
    };

    this.widevine = {
      licenseUrl: processEnv.APP_WIDEVINE_LICENSE_URL,
      licenseAccountUrl: processEnv.APP_WIDEVINE_LICENSE_ACCOUNT_PARAM_BASE_URL,
    };

    this.timers = {
      seriesExpiryDayLimit: constants.timers.seriesExpiryDayLimit,
      movieExpiryDayLimit: constants.timers.movieExpiryDayLimit,
    };

    this.login = {
      emailTemplates: constants.login.emailTemplates,
    };

    this.setupAnalyticConfigs();
    this.setupExternalServices();
    this.setupLocalServices();
  }

  private getService = <A extends unknown[], S>(
    service: {
      readonly serviceName: Parameters<LightningAppData['hasService']>[0];
    } & (new (...args: A) => S),
    ...args: A
  ): S | null => {
    if (!this.hasService(service.serviceName)) return null;
    return new service(...args);
  };

  private setupAnalyticConfigs() {
    this.brightcove = {
      accountId: processEnv.APP_BRIGHTCOVE_ACCOUNT_ID,
      adConfigId: processEnv.APP_BRIGHTCOVE_AD_CONFIG_ID,
      analyticsUrl: constants.brightcove.analyticsUrl,
      sdk: this.device.canUseBrightcoveSdk() ? loadBrightcoveSdkScript() : null,
    };

    this.conviva = {
      customerKey: processEnv.APP_CONVIVA_KEY,
      gatewayUrl: this.isProduction
        ? undefined
        : processEnv.APP_CONVIVA_GATEWAY_URL,
      mediaFileApiFramework: constants.conviva.mediaFileApiFramework,
      playerName: processEnv.APP_CONVIVA_PLAYER_NAME,
      technology: constants.conviva.technology,
      adStitcher: constants.conviva.adStitcher,
      brand: constants.conviva.brand,
    };

    this.comscore = {
      clientId: processEnv.APP_COMSCORE_CLIENT_ID,
      publisherId: processEnv.APP_COMSCORE_PUBLISHER_ID,
      publisherSecret: processEnv.APP_COMSCORE_PUBLISHER_SECRET,
      appName: constants.comscore.appName,
      brandName: constants.comscore.brandName,
      stationTitle: constants.comscore.stationTitle,
      videoMetrixC3: constants.comscore.videoMetrixC3,
    };

    this.nielsen = {
      appId: processEnv.APP_NIELSEN_APP_ID,
      instanceName: constants.nielsen.instanceName,
      baseUrl: processEnv.APP_NIELSEN_BASE_URL,
    };

    this.mParticle = {
      key: processEnv.APP_MPARTICLE_KEY,
      dataPlan: {
        id: constants.mparticle.dataPlanId,
      },
      mpid: '',
      sessionId: '',
    };
  }

  private setupExternalServices() {
    this.blueconicService = this.getService(BlueconicService, {
      baseUrl: processEnv.APP_BLUECONIC_BASE_URL,
    });

    this.personaService = this.getService(PersonaService, {
      apiKey: processEnv.APP_CW_PERSONA_API_KEY,
    });

    this.loginRadiusService = this.getService(
      LoginRadiusService,
      { apiKey: processEnv.APP_LOGIN_RADIUS_KEY },
      this.localHistoryService,
    );

    this.brazeService = this.getService(BrazeService);

    this.adobePassService = this.getService(AdobePassService, {
      baseUrl: processEnv.APP_ADOBE_PASS_URL,
    });

    this.oneTrust = {
      appId: processEnv.APP_ONETRUST_APP_ID,
      cdnLocation: processEnv.APP_ONETRUST_CDN_LOCATION,
      deviceType: constants.oneTrust.deviceType,
      forceBanner:
        !this.isProduction && processEnv.APP_ONETRUST_FORCE_BANNER === 'true',
      sdkVersion: constants.oneTrust.sdkVersion,
    };

    // Local service, but created here because OneTrustService depends on it
    this.privacyService = new PrivacyService({
      deviceIntegration: this.device,
      storageService: this.storageService,
    });

    // Tizen/webOS guard reads `deviceInfo.osVersion`, which is only populated by
    // `device.load()` during the router's `boot` hook.
    // `OneTrustService.init()` evaluates the guard itself.
    this.oneTrustService = this.getService(OneTrustService, {
      config: this.oneTrust,
      deviceIntegration: this.device,
      privacyService: this.privacyService,
    });
  }

  private setupLocalServices() {
    if (this.personaService && this.loginRadiusService) {
      this.qrLoginService = new QrLoginService();
      this.smartLoginService = new SmartLoginService(this.loginRadiusService);
    } else {
      this.qrLoginService = null;
      this.smartLoginService = null;
    }

    this.parentalPinService = new ParentalPinService({
      device: this.device,
      storageService: this.storageService,
    });

    this.recentSearchesService = new RecentSearchService({
      storageService: this.storageService,
    });

    this.migrationService = new MigrationService({
      deviceIntegration: this.device,
      historyService: this.localHistoryService,
      privacyService: this.privacyService,
      storageService: this.storageService,
    });

    this.offlineErrorService = new OfflineErrorService({
      deviceIntegration: this.device,
      storageService: this.storageService,
    });

    this.modalService = new ModalService();

    this.notificationService = new NotificationService();

    this.historyService = new HistoryService({
      storage: this.storageService,
      persona: this.personaService,
      local: this.localHistoryService,
      login: this.loginRadiusService,
    });

    this.listItemPromiseService = new ListItemPromiseService();
  }
}
